import { isEqual } from 'lodash';
import { useFiltersStore } from 'store/useFiltersStore';
import { PropertyFilterType, SavedFilter } from 'types/PropertyFilters';
import { FilterType } from 'util/Types';
import { validateAndCleanFilter } from 'util/filterValidation';
import { useToast } from 'components/Providers/ToastProvider';

const convertToFilterType = (filter: PropertyFilterType): FilterType => {
    return {
        country: filter.country,
        city: filter.city,
        state: filter.state,
        type: Array.isArray(filter.type) ? filter.type : filter.type ? [filter.type] : [],
        locations: filter.locations,
        price: {
            from: filter.price.from,
            to: filter.price.to,
        },
        constructionArea: {
            from: filter.constructionArea.from,
            to: filter.constructionArea.to,
        },
        plotArea: {
            from: filter.plotArea.from,
            to: filter.plotArea.to,
        },
        pricePerSquareMeter: {
            from: filter.pricePerSquareMeter.from,
            to: filter.pricePerSquareMeter.to,
        },
        bedbath: filter.bedbath,
        listingType: filter.listingType,
        neighborhood: filter.neighborhood,
        availability: filter.availability || ['available'],
    };
};

export const useFilterLogic = (childRef?: React.RefObject<{ closePopup: () => void }>) => {
    const { currentFilter, savedFilterList, setCurrentFilter } = useFiltersStore();
    const { showMessage } = useToast();

    const currentIndex = savedFilterList.findIndex((filter) => {
        // Normalize availability field for comparison
        const normalizedSavedFilter = {
            ...filter.filter,
            availability: filter.filter.availability || ['available'],
        };
        const normalizedCurrentFilter = {
            ...currentFilter,
            availability: currentFilter.availability || ['available'],
        };
        return isEqual(normalizedSavedFilter, normalizedCurrentFilter);
    });

    const onFilterClicked = (filter: SavedFilter) => {
        // Validate and clean the filter before applying it
        const validationResult = validateAndCleanFilter(filter.filter);

        if (validationResult.isCompletelyEmpty) {
            // Show error for completely invalid filter
            const errorMessage = `Filter "${filter.title}" is no longer valid. All filter values have been removed or are no longer available.`;
            showMessage('Error', errorMessage);
            return; // Don't apply the filter
        } else if (!validationResult.isValid) {
            // Show warning about partially invalid filter
            const warningMessage = `Filter "${filter.title}" contains some invalid values that have been removed:\n${validationResult.warnings.join('\n')}`;
            showMessage('Warning', warningMessage);
        } else if (validationResult.warnings.length > 0) {
            // Show info about cleaned filter
            const infoMessage = `Filter "${filter.title}" has been updated:\n${validationResult.warnings.join('\n')}`;
            showMessage('Info', infoMessage);
        }

        const convertedFilter = convertToFilterType(validationResult.cleanedFilter);
        setCurrentFilter(convertedFilter);
        if (childRef?.current) childRef.current.closePopup();
    };

    return {
        currentFilter,
        savedFilterList,
        currentIndex,
        onFilterClicked,
        setCurrentFilter,
    };
};
